// Pure decision rules of MainComponent, extracted so they can be unit tested without mounting it.
import { DisplayMode } from 'src/app/models/display-mode';
import { WindElement } from 'src/app/models/wind';
import { WebviewService } from 'src/app/services/platform/webview.service';

type Distance = (origin: number[], destination: number[]) => number;

/** Startup: the stored feedback counter, 0 when missing. */
export function initialFeedbackCounter(stored: string | null): number {
  return stored ? Number.parseInt(stored) : 0;
}

/** Startup: skipped chargers are cleared unless the load continues a car-handoff trip. */
export function clearsSkippedChargersAtStartup(startedFromCarHandoff: boolean): boolean {
  return !startedFromCarHandoff;
}

/** NoSleep is toggled only on mobile, non-iOS browsers outside the GPST webview (the Android app keeps the screen on itself). */
export function noSleepApplies(device: { mobile(): boolean; ios(): boolean }, webviewService: Pick<WebviewService, 'IsGPSTWebview'>): boolean {
  return device.mobile() && !webviewService.IsGPSTWebview() && !device.ios();
}

/** The wind element closest to the start point (the first one on ties). */
export function closestWindElement(start: { lat: number; lng: number }, windElements: WindElement[], distance: Distance): WindElement {
  let closestIdx = 0;
  let closestDist = distance([start.lat, start.lng], [windElements[0].Location.lat, windElements[0].Location.lng]);
  for (let i = 1; i < windElements.length; i++) {
    const d = distance([start.lat, start.lng], [windElements[i].Location.lat, windElements[i].Location.lng]);
    if (d < closestDist) {
      closestIdx = i;
      closestDist = d;
    }
  }
  return windElements[closestIdx];
}

/** Leaving the "line" (route) loading state outside navigation clears the map layers. */
export function clearsMapLayersOnLoadingChange(current: string | null, next: string | null, navigating: boolean): boolean {
  return current == "line" && next != "line" && !navigating;
}

/** Map repositioning when the mobile panel changes: opening the car sliders, and leaving a car panel. */
export function mapRepositioningForPanel(current: string, next: string, navigating: boolean): { open: boolean; close: boolean } {
  return {
    open: next === "car" && !navigating,
    close: (current === "car" || current === "car-open") && !navigating,
  };
}

/** The error dialog carries the low-battery warning in ROUTE mode below 10% (unknown battery counts as 100%). */
export function showsLowBatteryWarning(mode: DisplayMode | null, batteryLevel: number | undefined): boolean {
  return mode == DisplayMode.ROUTE && (batteryLevel ?? 100) < 10;
}

/** The first device position moves the start when there are no waypoints yet or the start is more than 2 km away. */
export function firstPositionMovesStart(waypoints: { lat: number; lng: number }[], position: { Latitude: number; Longitude: number }, distance: Distance): boolean {
  let lastPointDistance = Number.MAX_VALUE;
  if (waypoints.length > 0) {
    lastPointDistance = distance([waypoints[0].lat, waypoints[0].lng], [position.Latitude, position.Longitude]);
  }
  return lastPointDistance > 2000;
}

export type GeolocationStartAction = 'center' | 'setStart' | 'userFly' | 'flyTo';

/**
 * setStartCoordsToYourGeolocation: without a position (or while navigating) the map is centred; otherwise the start
 * waypoint is replaced when follow mode is off or this is the first position, a user click flies the map, and
 * anything else just re-centres on the position.
 */
export function geolocationStartAction(navigating: boolean, hasPosition: boolean, followModeRange: boolean, setByUserClick?: boolean, firstPosition?: boolean): GeolocationStartAction {
  if (navigating || !hasPosition) {
    return 'center';
  }
  if (!followModeRange || firstPosition) {
    return 'setStart';
  }
  return setByUserClick ? 'userFly' : 'flyTo';
}
